/*
 * Infinity Options add-on enhancement.
 * Keeps paid services opt-in, mirrors the approved add-on card structure,
 * and renders market-aware prices from linked Shopify products.
 */
(() => {
  const selector = '.ninja-addon-hook-change, .ninja-addon-giftpack';
  const initialized = new WeakSet();
  const productCache = new Map();

  const choices = [
    {
      matches: (value, group) => group.classList.contains('ninja-addon-giftpack') || /lahja|gift/i.test(value),
      handle: 'lahjapaketti-402010000500',
      labels: {
        fi: ['Lahjapaketti', 'Puuvillainen säilytyspussi'],
        sv: ['Presentförpackning', 'Förvaringspåse i bomull'],
        en: ['Gift packaging', 'Cotton storage pouch'],
        de: ['Geschenkverpackung', 'Aufbewahrungsbeutel aus Baumwolle']
      }
    },
    {
      matches: (value) => /klipsienvaihto/i.test(value),
      handle: 'teraksiset-klipsien-vaihto-korvakoruihin-teras-316l',
      labels: {
        fi: ['Klipsikiinnitys', 'Kirurginteräs'],
        sv: ['Clipsfäste', 'Kirurgiskt stål'],
        en: ['Clip-on conversion', 'Surgical steel'],
        de: ['Clip-Verschluss', 'Chirurgenstahl']
      }
    },
    {
      matches: (value) => /koukut/i.test(value),
      handle: 'teraskoukkujen-vaihto-10507000015v',
      labels: {
        fi: ['Teräskoukut', 'Kirurginteräs'],
        sv: ['Öronkrokar', 'Kirurgiskt stål'],
        en: ['Earring hooks', 'Surgical steel'],
        de: ['Ohrhaken', 'Chirurgenstahl']
      }
    }
  ];

  const language = () => {
    const code = (document.documentElement.lang || 'fi').toLowerCase().split('-')[0];
    return ['fi', 'sv', 'en', 'de'].includes(code) ? code : 'en';
  };

  const routesRoot = () => window.Shopify?.routes?.root || '/';

  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      const url = routesRoot() + 'products/' + handle + '.js';
      productCache.set(
        handle,
        fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        }).then((response) => {
          if (!response.ok) throw new Error('Product request failed: ' + response.status);
          return response.json();
        })
      );
    }
    return productCache.get(handle);
  };

  const currentCurrency = () =>
    window.Shopify?.currency?.active ||
    document.querySelector('meta[property="og:price:currency"]')?.content ||
    'EUR';

  const formatMoney = (cents) => {
    const locale = document.documentElement.lang || navigator.language || 'fi-FI';
    const currency = currentCurrency();

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2) + ' ' + currency;
    }
  };

  const emitChange = (input) => {
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  const clearChoice = (input) => {
    input.checked = false;
    emitChange(input);
  };

  const renderChoice = async (group, input, config) => {
    const card = input.closest('.spb-productoptionswatchwrapper');
    const label = card?.querySelector('.spb-swatchlabel');
    if (!card || !label) return;

    const text = config.labels[language()] || config.labels.en;
    const copy = document.createElement('span');
    const nameElement = document.createElement('span');
    const metaElement = document.createElement('span');
    const priceElement = document.createElement('span');

    copy.className = 'ninja-addon-copy';
    nameElement.className = 'ninja-addon-name';
    metaElement.className = 'ninja-addon-meta';
    priceElement.className = 'ninja-addon-price is-loading';
    priceElement.setAttribute('aria-live', 'polite');

    nameElement.textContent = text[0];
    metaElement.textContent = text[1];
    priceElement.textContent = '…';
    copy.append(nameElement, metaElement, priceElement);
    label.replaceChildren(copy);

    try {
      const product = await fetchProduct(config.handle);
      const variant = product.variants?.find((item) => item.available) || product.variants?.[0];

      if (!variant || variant.available === false) {
        card.hidden = true;
      } else {
        priceElement.textContent = formatMoney(variant.price);
        priceElement.classList.remove('is-loading');
      }
    } catch (error) {
      priceElement.remove();
    }

    const visibleCards = [...group.querySelectorAll('.spb-productoptionswatchwrapper')]
      .filter((item) => !item.hidden);
    group.hidden = visibleCards.length === 0;
  };

  const initialize = (group) => {
    if (initialized.has(group)) return;
    initialized.add(group);

    if (group.classList.contains('ninja-addon-giftpack')) {
      group.classList.add('ninja-addon-hook-change');
    }

    const inputs = [...group.querySelectorAll('input[type="radio"], input[type="checkbox"]')];
    if (!inputs.length) return;

    const preselected = inputs.find((input) => input.checked);
    if (preselected) clearChoice(preselected);

    inputs.forEach((input) => {
      const card = input.closest('.spb-productoptionswatchwrapper');
      const value = String(input.value || '');
      const config = choices.find((choice) => choice.matches(value, group));
      if (!card) return;

      if (!card.querySelector('.ninja-addon-check')) {
        const check = document.createElement('span');
        check.className = 'ninja-addon-check';
        check.setAttribute('aria-hidden', 'true');
        card.append(check);
      }

      if (config) renderChoice(group, input, config);

      card.addEventListener('pointerdown', () => {
        card.dataset.ninjaWasChecked = String(input.checked);
      });

      card.addEventListener('click', (event) => {
        if (card.dataset.ninjaWasChecked === 'true') {
          event.preventDefault();
          clearChoice(input);
        }
        delete card.dataset.ninjaWasChecked;
      });

      input.addEventListener('keydown', (event) => {
        if ((event.key === ' ' || event.key === 'Enter') && input.checked) {
          event.preventDefault();
          clearChoice(input);
        }
      });
    });
  };

  const scan = (root = document) => {
    if (root.matches?.(selector)) initialize(root);
    root.querySelectorAll?.(selector).forEach(initialize);
  };

  const start = () => {
    scan();
    new MutationObserver((records) => {
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) scan(node);
        });
      });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/*
 * Klaviyo legacy Back in Stock visibility sync.
 * The legacy Klaviyo script can inject its trigger independently of the theme's
 * variant availability state. Keep the trigger hidden for purchasable variants
 * and show it only when the selected variant is actually sold out.
 */
(() => {
  const sectionSelector = '.product-section';
  const triggerSelector = '.klaviyo_backinstock';
  const observedSections = new WeakSet();

  const getSelectedVariant = (section) => {
    const select = section.querySelector('[data-product-select]');
    const variantsElement = section.querySelector('[data-variant-json]');
    if (!select || !variantsElement) return null;

    try {
      const variants = JSON.parse(variantsElement.value || variantsElement.textContent || '[]');
      const selectedId = String(select.value || '');
      return variants.find((variant) => String(variant.id) === selectedId) || null;
    } catch (error) {
      return null;
    }
  };

  const isSoldOut = (section) => {
    const variant = getSelectedVariant(section);
    if (variant) return variant.available === false;

    const addButton = section.querySelector('[data-add-to-cart]');
    return Boolean(addButton && addButton.disabled);
  };

  const setTriggerVisibility = (trigger, visible) => {
    trigger.style.setProperty('display', visible ? 'block' : 'none', 'important');
    trigger.style.setProperty('width', '100%', 'important');
    trigger.style.setProperty('max-width', 'none', 'important');
    trigger.setAttribute('aria-hidden', visible ? 'false' : 'true');

    if (visible) {
      trigger.removeAttribute('tabindex');
    } else {
      trigger.setAttribute('tabindex', '-1');
    }
  };

  const syncSection = (section) => {
    if (!section) return;
    const soldOut = isSoldOut(section);
    section.querySelectorAll(triggerSelector).forEach((trigger) => {
      setTriggerVisibility(trigger, soldOut);
    });
  };

  const syncOrphanTriggers = () => {
    document.querySelectorAll(triggerSelector).forEach((trigger) => {
      const section = trigger.closest(sectionSelector);
      if (section) {
        syncSection(section);
        return;
      }

      const form = trigger.closest('.product-single__form');
      const addButton = form?.querySelector('[data-add-to-cart]');
      setTriggerVisibility(trigger, Boolean(addButton && addButton.disabled));
    });
  };

  const observeSection = (section) => {
    if (observedSections.has(section)) return;
    observedSections.add(section);

    section.addEventListener('change', () => {
      window.setTimeout(() => syncSection(section), 0);
      window.setTimeout(() => syncSection(section), 120);
    });

    new MutationObserver(() => {
      syncSection(section);
    }).observe(section, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled']
    });

    syncSection(section);
  };

  const scan = (root = document) => {
    if (root.matches?.(sectionSelector)) observeSection(root);
    root.querySelectorAll?.(sectionSelector).forEach(observeSection);
    syncOrphanTriggers();
  };

  const start = () => {
    scan();

    new MutationObserver((records) => {
      let shouldSync = false;

      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          if (node.matches?.(sectionSelector) || node.querySelector?.(sectionSelector)) {
            scan(node);
          }
          if (node.matches?.(triggerSelector) || node.querySelector?.(triggerSelector)) {
            shouldSync = true;
          }
        });
      });

      if (shouldSync) syncOrphanTriggers();
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
